(() => {
  const V = window.Vernika = window.Vernika || {};
  const RECENT_KEY = "vernika:recent";
  const reduceMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const fill = (template, values) => template.replace(/%(\w+)%/g, ((_, key) => values[key] ?? ""));
  class Gallery {
    constructor(root, lightbox) {
      this.root = root;
      this.track = root.querySelector("[data-gallery-track]");
      this.slides = [ ...this.track.children ];
      this.thumbs = [ ...root.querySelectorAll("[data-thumb]") ];
      this.dots = [ ...root.querySelectorAll(".gallery__dot") ];
      this.counter = root.querySelector("[data-gallery-current]");
      this.prev = root.querySelector("[data-gallery-prev]");
      this.next = root.querySelector("[data-gallery-next]");
      this.lightbox = lightbox ? new Lightbox(lightbox) : null;
      this.index = 0;
      if (this.slides.length > 1) {
        const observer = new IntersectionObserver((entries => entries.forEach((e => e.isIntersecting && this.setActive(this.slides.indexOf(e.target))))), {
          root: this.track,
          threshold: .6
        });
        this.slides.forEach((slide => observer.observe(slide)));
      }
      this.thumbs.forEach((thumb => thumb.addEventListener("click", (() => this.show(thumb.dataset.thumb)))));
      this.prev?.addEventListener("click", (() => this.goTo(this.index - 1)));
      this.next?.addEventListener("click", (() => this.goTo(this.index + 1)));
      root.addEventListener("click", (event => {
        const zoom = event.target.closest("[data-zoom-index]");
        if (zoom && this.lightbox) this.lightbox.open(Number(zoom.dataset.zoomIndex), zoom);
      }));
      this.magnifier();
      this.setActive(0);
    }
    setActive(i) {
      if (i < 0) return;
      this.index = i;
      this.thumbs.forEach(((thumb, n) => thumb.setAttribute("aria-current", String(n === i))));
      this.dots.forEach(((dot, n) => dot.classList.toggle("is-active", n === i)));
      if (this.counter) this.counter.textContent = i + 1;
      if (this.prev) this.prev.disabled = i === 0;
      if (this.next) this.next.disabled = i === this.slides.length - 1;
      this.slides.forEach(((slide, n) => {
        if (n !== i) slide.querySelector("video")?.pause();
      }));
    }
    goTo(i, smooth = true) {
      const index = Math.max(0, Math.min(i, this.slides.length - 1));
      this.track.scrollTo({
        left: this.slides[index].offsetLeft,
        behavior: smooth && !reduceMotion() ? "smooth" : "auto"
      });
      this.setActive(index);
    }
    show(mediaId, smooth = true) {
      const i = this.slides.findIndex((slide => slide.dataset.mediaId === String(mediaId)));
      if (i > -1) this.goTo(i, smooth);
    }
    magnifier() {
      const fine = window.matchMedia("(hover: hover) and (pointer: fine)");
      this.root.querySelectorAll(".gallery__zoom").forEach((zone => {
        const img = zone.querySelector("img");
        if (!img) return;
        zone.addEventListener("pointerenter", (event => {
          if (event.pointerType === "mouse" && fine.matches && window.innerWidth >= 990) zone.classList.add("is-magnified");
        }));
        zone.addEventListener("pointermove", (event => {
          if (!zone.classList.contains("is-magnified")) return;
          const rect = zone.getBoundingClientRect();
          img.style.transformOrigin = `${(event.clientX - rect.left) / rect.width * 100}% ${(event.clientY - rect.top) / rect.height * 100}%`;
        }));
        zone.addEventListener("pointerleave", (() => zone.classList.remove("is-magnified")));
      }));
    }
  }
  class Lightbox {
    constructor(dialog) {
      this.dialog = dialog;
      this.track = dialog.querySelector("[data-lightbox-track]");
      this.slides = [ ...this.track.children ];
      dialog.addEventListener("click", (event => {
        const frame = event.target.closest("[data-lightbox-frame]");
        if (!frame || event.target.closest("[data-dialog-close]")) return;
        this.toggleZoom(frame, event);
      }));
      dialog.addEventListener("close", (() => this.reset()));
    }
    open(index, opener) {
      if (!this.slides.length) return;
      V.openDialog(this.dialog.id, opener);
      requestAnimationFrame((() => {
        const slide = this.slides[Math.min(index, this.slides.length - 1)];
        this.track.scrollTo({
          left: slide.offsetLeft,
          behavior: "auto"
        });
      }));
    }
    toggleZoom(frame, event) {
      const img = frame.querySelector("img");
      if (!img) return;
      if (frame.classList.contains("is-zoomed")) {
        frame.classList.remove("is-zoomed");
        this.dialog.classList.remove("has-zoom");
        return;
      }
      const rect = img.getBoundingClientRect();
      const x = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
      const y = Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height));
      frame.classList.add("is-zoomed");
      this.dialog.classList.add("has-zoom");
      requestAnimationFrame((() => {
        frame.scrollLeft = img.offsetWidth * x - frame.clientWidth / 2;
        frame.scrollTop = img.offsetHeight * y - frame.clientHeight / 2;
      }));
    }
    reset() {
      this.dialog.classList.remove("has-zoom");
      this.dialog.querySelectorAll(".is-zoomed").forEach((frame => frame.classList.remove("is-zoomed")));
    }
  }
  class ProductPage {
    constructor(root) {
      this.root = root;
      this.threshold = Number(root.dataset.threshold) || 3;
      this.variants = JSON.parse(root.querySelector("[data-product-variants]")?.textContent || "[]");
      this.s = JSON.parse(root.querySelector("[data-product-strings]")?.textContent || "{}");
      this.form = root.querySelector("[data-product-form]");
      this.fieldsets = [ ...root.querySelectorAll("[data-option-index]") ];
      this.selected = this.fieldsets.map((fs => fs.querySelector("input:checked")?.value ?? null));
      this.current = this.find();
      this.gallery = root.querySelector("[data-gallery]") ? new Gallery(root.querySelector("[data-gallery]"), root.querySelector("[data-lightbox]")) : null;
      root.addEventListener("change", (event => {
        if (event.target.matches("[data-option-input]")) this.onOption(event.target);
      }));
      this.form?.addEventListener("submit", (event => this.onSubmit(event)));
      root.addEventListener("click", (event => {
        const share = event.target.closest("[data-share]");
        if (share) this.share(share);
      }));
      this.updateAvailability();
      if (this.current?.media && this.gallery) this.gallery.show(this.current.media, false);
      this.sticky();
      this.remember();
    }
    find() {
      if (!this.fieldsets.length) {
        const id = this.root.querySelector("[data-variant-id]")?.value;
        return this.variants.find((v => String(v.id) === id)) || this.variants.find((v => v.available)) || this.variants[0] || null;
      }
      if (this.selected.some((value => value === null))) return null;
      return this.variants.find((v => v.options.every(((option, i) => option === this.selected[i])))) || null;
    }
    onOption(input) {
      const fieldset = input.closest("[data-option-index]");
      const i = Number(fieldset.dataset.optionIndex);
      this.selected[i] = input.value;
      const label = fieldset.querySelector("[data-option-value]");
      if (label) label.textContent = `: ${input.value}`;
      this.root.querySelector("[data-option-error]")?.setAttribute("hidden", "");
      this.current = this.find();
      this.update();
    }
    update() {
      const v = this.current;
      const idInput = this.root.querySelector("[data-variant-id]");
      if (idInput) {
        idInput.value = v ? v.id : "";
        idInput.disabled = !v;
      }
      this.renderPrice(v);
      this.renderStock(v);
      this.renderButtons(v);
      this.updateAvailability();
      this.root.querySelectorAll("[data-product-sku]").forEach((row => {
        const sku = v?.sku || "";
        row.hidden = !sku;
        const span = row.querySelector("span");
        if (span) span.textContent = sku;
      }));
      const dynamic = this.root.querySelector("[data-dynamic-checkout]");
      if (dynamic) dynamic.hidden = !v || !v.available;
      const notify = this.root.querySelector("[data-notify]");
      if (notify) notify.hidden = !v || v.available;
      const notifyVariant = this.root.querySelector("[data-notify-variant]");
      if (notifyVariant && v) notifyVariant.value = v.title;
      if (v) {
        const url = new URL(window.location.href);
        url.searchParams.set("variant", v.id);
        history.replaceState(history.state, "", url);
        if (v.media && this.gallery) this.gallery.show(v.media);
      }
    }
    renderPrice(v) {
      const box = this.root.querySelector("[data-product-price]");
      const sticky = this.root.querySelector("[data-sticky-price]");
      if (!v) return;
      if (sticky) sticky.textContent = V.money(v.price);
      if (!box) return;
      const sale = v.compare > v.price;
      const wrap = document.createElement("div");
      wrap.className = `price price--lg${sale ? " price--sale" : ""}`;
      wrap.dataset.price = "";
      const now = document.createElement("span");
      now.className = "price__now";
      now.textContent = V.money(v.price);
      wrap.append(now);
      if (sale) {
        const was = document.createElement("s");
        was.className = "price__was";
        was.textContent = V.money(v.compare);
        const off = document.createElement("span");
        off.className = "price__off";
        off.textContent = fill(this.s.off, {
          PERCENT: Math.round((v.compare - v.price) * 100 / v.compare)
        });
        wrap.append(was, off);
      }
      if (this.s.taxNote) {
        const tax = document.createElement("span");
        tax.className = "price__tax";
        tax.textContent = this.s.taxNote;
        wrap.append(tax);
      }
      box.replaceChildren(wrap);
    }
    renderStock(v) {
      const el = this.root.querySelector("[data-product-stock]");
      if (!el) return;
      el.className = "product__stock";
      if (!v) {
        el.hidden = true;
        return;
      }
      el.hidden = false;
      if (!v.available) {
        el.classList.add("is-out");
        el.textContent = this.s.soldOut;
      } else if (v.stock !== null && v.stock <= this.threshold) {
        el.classList.add("is-low");
        el.textContent = v.stock === 1 ? this.s.lowOne : fill(this.s.lowOther, {
          COUNT: v.stock
        });
      } else {
        el.classList.add("is-in");
        el.textContent = this.s.inStock;
      }
    }
    missingOption() {
      const i = this.selected.findIndex((value => value === null));
      return i === -1 ? null : this.fieldsets[i];
    }
    buttonLabel(v) {
      const missing = this.missingOption();
      if (missing) return fill(this.s.select, {
        OPTION: missing.dataset.optionName.toLowerCase()
      });
      if (!v || !v.available) return this.s.soldOut;
      return this.s.addToBag;
    }
    renderButtons(v) {
      const label = this.buttonLabel(v);
      const add = this.root.querySelector("[data-add-button]");
      if (add) {
        add.disabled = Boolean(v && !v.available) || !v && !this.missingOption();
        const span = add.querySelector("[data-add-label]");
        if (span) span.textContent = label;
      }
      const stickyLabel = this.root.querySelector("[data-sticky-label]");
      if (stickyLabel) stickyLabel.textContent = label;
      const stickyButton = this.root.querySelector("[data-sticky-button]");
      if (stickyButton) stickyButton.disabled = Boolean(v && !v.available);
    }
    updateAvailability() {
      this.fieldsets.forEach(((fieldset, i) => {
        fieldset.querySelectorAll("[data-option-input]").forEach((input => {
          const possible = this.variants.some((v => v.available && v.options[i] === input.value && v.options.every(((o, j) => j === i || this.selected[j] === null || this.selected[j] === o))));
          input.closest(".chip")?.classList.toggle("chip--unavailable", !possible);
        }));
      }));
    }
    showMissing() {
      const fieldset = this.missingOption();
      if (!fieldset) return;
      const error = this.root.querySelector("[data-option-error]");
      if (error) {
        error.querySelector("span").textContent = fill(this.s.pleaseSelect, {
          OPTION: fieldset.dataset.optionName.toLowerCase()
        });
        error.hidden = false;
      }
      fieldset.scrollIntoView({
        behavior: reduceMotion() ? "auto" : "smooth",
        block: "center"
      });
      fieldset.classList.remove("is-shaking");
      void fieldset.offsetWidth;
      fieldset.classList.add("is-shaking");
      fieldset.querySelector("input")?.focus({
        preventScroll: true
      });
    }
    async onSubmit(event) {
      event.preventDefault();
      if (!this.current) {
        this.showMissing();
        return;
      }
      if (!this.current.available) return;
      const button = this.root.querySelector("[data-add-button]");
      const quantity = Number(this.root.querySelector("[data-quantity]")?.value || 1);
      const properties = {};
      new FormData(this.form).forEach(((value, key) => {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && String(value).trim() !== "") properties[match[1]] = value;
      }));
      button?.setAttribute("aria-busy", "true");
      try {
        await V.cart.add([ {
          id: this.current.id,
          quantity: quantity,
          properties: properties
        } ]);
      } catch (error) {
        V.toast(error.message, {
          tone: "error"
        });
      } finally {
        button?.removeAttribute("aria-busy");
      }
    }
    sticky() {
      const bar = this.root.querySelector("[data-sticky-atc]");
      const main = this.root.querySelector("[data-add-button]");
      if (!bar || !main) return;
      const button = bar.querySelector("[data-sticky-button]");
      const observer = new IntersectionObserver((([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        bar.classList.toggle("is-visible", show);
        bar.setAttribute("aria-hidden", String(!show));
        button.tabIndex = show ? 0 : -1;
        document.documentElement.style.setProperty("--toast-offset", show && window.innerWidth < 990 ? "86px" : "16px");
      }));
      observer.observe(main);
      button.addEventListener("click", (() => {
        if (!this.current) {
          this.showMissing();
          return;
        }
        this.form.requestSubmit();
      }));
    }
    async share(button) {
      const data = {
        title: button.dataset.shareTitle,
        url: button.dataset.shareUrl
      };
      try {
        if (navigator.share) {
          await navigator.share(data);
        } else {
          await navigator.clipboard.writeText(data.url);
          V.toast(button.dataset.copied);
        }
      } catch {}
    }
    remember() {
      const handle = this.root.dataset.handle;
      if (!handle || !V.store) return;
      const list = V.store.get(RECENT_KEY, []).filter((h => h !== handle));
      list.unshift(handle);
      V.store.set(RECENT_KEY, list.slice(0, 12));
    }
  }
  const init = (scope = document) => {
    scope.querySelectorAll("[data-product]:not([data-ready])").forEach((root => {
      root.dataset.ready = "";
      new ProductPage(root);
    }));
  };
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", (() => init())); else init();
  document.addEventListener("shopify:section:load", (event => init(event.target)));
})();
